/**
 * Settings, kept in localStorage.
 *
 * The desktop's main process owned one settings file and broadcast every change
 * to every window, numbered, so a window could tell a stale copy from a fresh
 * one. Here every tab reads and writes the same localStorage entry, and the
 * `storage` event is the broadcast: it reaches every other tab of the origin.
 * The revision travels with the settings, so it counts changes across tabs.
 *
 * localStorage rather than IndexedDB because it can be read synchronously:
 * the theme is known before the first paint, so a dark theme never flashes
 * white.
 */
import {
  DEFAULT_SETTINGS,
  applySettingsPatch,
  mergeSettings,
  type Settings,
  type SettingsPatch,
  type SettingsState,
} from '../shared/settings'

const KEY = 'ekram-md:settings'

type Listener = (s: SettingsState) => void
const listeners = new Set<Listener>()

interface Stored {
  revision: number
  settings: unknown
}

/** What is stored now; defaults when nothing is, or what is cannot be read. */
function read(): SettingsState {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(KEY)
  } catch {
    // Storage blocked (a private window, a policy): run on defaults.
  }
  if (!raw) return { ...structuredClone(DEFAULT_SETTINGS), revision: 0 }
  try {
    const stored = JSON.parse(raw) as Stored
    return { ...forThisPage(mergeSettings(stored.settings)), revision: stored.revision ?? 0 }
  } catch {
    // Unreadable: kept aside rather than overwritten, as the desktop does with a
    // corrupt settings file, so a bad write does not cost every setting.
    try {
      localStorage.setItem(`${KEY}.corrupt-${Date.now()}`, raw)
    } catch {
      /* nothing more to do */
    }
    return { ...structuredClone(DEFAULT_SETTINGS), revision: 0 }
  }
}

/**
 * Settings that only make sense where they were made: a folder read as a
 * snapshot (no File System Access) is gone once its page is, so it is never
 * reopened, offered as recent, or restored with the session.
 */
function forThisPage(s: Settings): Settings {
  const lasting = (p: string): boolean => !p.startsWith('snap:')
  return {
    ...s,
    workspace: s.workspace && lasting(s.workspace) ? s.workspace : null,
    recentFiles: s.recentFiles.filter(lasting),
    session: {
      ...s.session,
      files: s.session.files.filter(lasting),
      active: s.session.active && lasting(s.session.active) ? s.session.active : null,
    },
  }
}

function write(state: SettingsState): void {
  const { revision, ...settings } = state
  try {
    localStorage.setItem(KEY, JSON.stringify({ revision, settings } satisfies Stored))
  } catch {
    // Storage full or blocked: the change still applies to this page.
  }
}

/** The page's copy: what this tab last read or wrote. */
let current: SettingsState | null = null

export function getSettings(): SettingsState {
  if (!current) current = read()
  return current
}

/**
 * Applies a patch over the newest stored copy, one group deep, as the desktop's
 * main process did. Read fresh first: another tab may have written since.
 */
export function patchSettings(patch: SettingsPatch): SettingsState {
  const latest = read()
  const base = current && current.revision > latest.revision ? current : latest
  const { revision, ...settings } = base
  const next: SettingsState = { ...applySettingsPatch(settings, patch), revision: revision + 1 }
  write(next)
  // A snapshot folder is still this tab's to show, though it is never stored:
  // reading back strips it, so it is carried over from this tab's copy.
  if (!('workspace' in patch) && current?.workspace?.startsWith('snap:')) {
    next.workspace = current.workspace
  }
  current = next
  for (const fn of listeners) fn(next)
  return next
}

export function onSettingsChanged(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY || e.newValue === null) return
    const next = read()
    // The workspace is per tab where it cannot be stored: keep this tab's.
    if (current?.workspace?.startsWith('snap:')) next.workspace = current.workspace
    current = next
    for (const fn of listeners) fn(next)
  })
}
